<template src="./TicketCard.html"></template>

<script>
import moment from 'moment-timezone'
import {
  getTicketPriority, getTicketPriorityColor, getTicketPriorityIcon, getTicketStatus, getTicketStatusColor
} from '../../utils/computeds'
import { dueUrgency, formatDueDate, relativeDueText } from '../../utils/dueDate'

// XAN-24 / XAN-193 (adjustments) — the ticket card for both the board and "Mis pendientes".
//
// One component so the two screens read the same way. There is no per-project colour: with a
// handful of colours two projects end up sharing one often, and a shared colour tells the reader
// "same project" when it is not. The project name is printed instead.
//
// It carries what is needed to act without opening the ticket: which project it belongs to, who
// holds it, priority, how the due date stands against today, a glimpse of the description, and how
// recently it moved. Only fields the list endpoint already returns are used, so the card costs no
// extra request.
//
// `context` changes just two things, each because the surrounding screen already answers it:
// - board: the column is the status, so the status chip is hidden; the person shown is the assignee.
// - personal: every ticket is the viewer's own, so showing the assignee would say "you" on every
//   card; the person shown is who created it instead, which is the useful counterpart.
export default {
  name: 'TicketCard',
  props: {
    ticket: {
      type: Object,
      required: true
    },
    context: {
      type: String,
      default: 'board',
      validator: (value) => ['board', 'personal'].includes(value)
    },
    now: {
      type: Date,
      default: () => new Date()
    }
  },
  computed: {
    getTicketPriority,
    getTicketPriorityColor,
    getTicketPriorityIcon,
    getTicketStatus,
    getTicketStatusColor,
    showStatus () {
      return this.context === 'personal' && this.ticket.status !== 'DONE'
    },
    urgency () {
      if (this.ticket.status === 'DONE') return 'done'
      return dueUrgency(this.ticket, this.now)
    },
    dueText () {
      if (this.urgency === 'done') return 'Completado'
      return relativeDueText(this.ticket.dueDate, this.now)
    },
    dueIcon () {
      switch (this.urgency) {
        case 'overdue': return 'mdi-alert-circle'
        case 'today': return 'mdi-clock-outline'
        case 'done': return 'mdi-check-circle-outline'
        case 'undated': return 'mdi-calendar-remove-outline'
        default: return 'mdi-calendar-blank-outline'
      }
    },
    projectName () {
      return this.ticket.project ? this.ticket.project.name : 'Sin proyecto'
    },
    // A ticket can hold several assignees, and the board's filter by person matches any of them — so
    // a card can show up because of someone other than the one it leads with. Hence the "+N": without
    // it, filtering by one person looks like a board full of other people's tickets (XAN-208).
    // Falls back to the primary assignee alone for a payload that predates the `assignees` field.
    assignees () {
      if (this.context === 'personal') return []
      const assignees = this.ticket.assignees?.length ? this.ticket.assignees : [this.ticket.assignedTo]
      return assignees.filter(Boolean)
    },
    extraAssigneeCount () {
      return Math.max(this.assignees.length - 1, 0)
    },
    person () {
      return this.context === 'personal' ? this.ticket.createdBy : this.assignees[0] || null
    },
    personLabel () {
      return this.context === 'personal' ? 'Creado por' : 'Asignado a'
    },
    personTitle () {
      const people = this.context === 'personal'
        ? [this.person].filter(Boolean)
        : this.assignees
      return `${this.personLabel}: ${people.length ? people.map((person) => this.fullName(person)).join(', ') : 'Sin asignar'}`
    },
    // Compact on purpose ("hace 22 min" rather than "hace 22 minutos"): the footer shares its one
    // line with the person's name, which is the part worth the space. Past a month the exact date
    // says more than "hace 3 meses".
    updatedRelative () {
      const updated = moment(this.ticket.updatedAt)
      const minutes = moment(this.now).diff(updated, 'minutes')
      if (minutes < 1) return 'ahora'
      if (minutes < 60) return `hace ${minutes} min`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `hace ${hours} h`
      const days = Math.floor(hours / 24)
      if (days < 30) return `hace ${days} d`
      return updated.tz('America/Bogota').format('DD/MM/YYYY')
    },
    updatedExact () {
      return moment(this.ticket.updatedAt).tz('America/Bogota').format('DD/MM/YYYY hh:mm a')
    }
  },
  methods: {
    formatDueDate,
    fullName (person) {
      return `${person.name} ${person.lastName || ''}`.trim()
    },
    initials (person) {
      return `${person.name?.charAt(0) || ''}${person.lastName?.charAt(0) || ''}`.toUpperCase() || '?'
    },
    onClick () {
      this.$emit('open', this.ticket)
    }
  }
}
</script>

<style lang="scss" src="./TicketCard.scss" scoped></style>
